import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Card, styled, Typography } from '@mui/material'
import { sanitize } from 'dompurify'
import type { FC } from 'react'

import { isNull } from '@hcr/utils'

import { COLORS, ICONS } from '../../../../constants'
import type { Link } from '../../../../models'
import { isLibPropValid, unit } from '../../../../utils'
import { UnstyledLink } from '../../../navigation'
import { CardEmbedment, CardVariant } from '../types'

export interface CardPromotionProps {
  description: string
  embedment: CardEmbedment
  link: Link | null
  title: string
  variant: CardVariant
}

export const CardPromotion: FC<CardPromotionProps> = ({ description, embedment, link, title, variant }) => {
  const content = (
    <Content>
      <Title
        cardVariant={variant}
        variant={embedment === CardEmbedment.Standalone ? 'headlineL' : 'displayM'}
        dangerouslySetInnerHTML={{ __html: sanitize(title) }}
      />
      <Typography color={COLORS.white} variant='bodyM' dangerouslySetInnerHTML={{ __html: sanitize(description) }} />
    </Content>
  )

  if (isNull(link)) {
    return (
      <Container cardEmbedment={embedment} cardVariant={variant}>
        {content}
      </Container>
    )
  }

  return (
    <UnstyledLink to={link.to}>
      <Container cardEmbedment={embedment} cardVariant={variant} clickable>
        {content}
        <CtaButton endIcon={<FontAwesomeIcon icon={ICONS.farArrowRight} />} variant='text' color='white'>
          {link.label}
        </CtaButton>
      </Container>
    </UnstyledLink>
  )
}

const Container = styled(Card, { shouldForwardProp: isLibPropValid })<{
  readonly cardEmbedment: CardEmbedment
  readonly cardVariant: CardVariant
  readonly clickable?: boolean
}>`
  padding: ${unit(6.5)} ${unit(6.5)} ${unit(6.5)} ${unit(5)};
  background-color: ${props => (props.cardVariant === CardVariant.Regular ? COLORS.red.dark : COLORS.sammal[500])};
  height: ${props => (props.cardEmbedment === CardEmbedment.Standalone ? 'auto' : unit(97.5))};
  width: ${props => (props.cardEmbedment === CardEmbedment.Standalone ? '100%' : unit(70))};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  word-break: break-word;

  ${props => props.clickable && { cursor: 'pointer' }};
`

const Content = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${unit(2)};
`

const Title = styled(Typography, { shouldForwardProp: isLibPropValid })<{ readonly cardVariant: CardVariant }>`
  color: ${COLORS.white};

  // Note: Below selector depends on the content received from Optimizely
  em {
    color: ${props => (props.cardVariant === CardVariant.Regular ? COLORS.red.bright : COLORS.green[500])};
  }
`

const CtaButton = styled(Button)`
  text-transform: uppercase;
  justify-content: flex-end;
  margin-top: ${unit(9)};
`
